import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import ReactApexChart from 'react-apexcharts';
import dayjs from 'dayjs';import ContentCard from "../../../components/shared/ContentCard";

const fmt = (n) =>
new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

// ─── CSV download helper ──────────────────────────────────────────────────────
function downloadCSV(filename, headers, rows) {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [headers, ...rows].map((row) => row.map(escape).join(','));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Report card ──────────────────────────────────────────────────────────────
function ReportCard({ title, description, onExport }) {
  return (
    <ContentCard
      sx={{ p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}>
      
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', flex: 1 }}>
        {description}
      </Typography>
      <Box sx={{ mt: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          onClick={onExport}
          sx={{ textTransform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 600,
            borderColor: 'var(--grey-300)',
            color: 'var(--grey-700)',
            '&:hover': { borderColor: '#009688', color: '#009688' }
          }}>
          
          Export CSV
        </Button>
      </Box>
    </ContentCard>);

}

// ─── Tab ──────────────────────────────────────────────────────────────────────
export default function FinancialReportsTab({
  filteredPos,
  filteredPayments,
  filteredShipping,
  filteredTickets,
  startDate,
  endDate
}) {
  const dateLabel = `${startDate} to ${endDate}`;

  // Export: PO Summary grouped by vendor
  const exportPOSummary = () => {
    const grouped = {};
    filteredPos.forEach((p) => {
      if (!grouped[p.vendorName]) grouped[p.vendorName] = { count: 0, total: 0 };
      grouped[p.vendorName].count += 1;
      grouped[p.vendorName].total += p.totalCost;
    });
    const rows = filteredPos.map((p) => [p.poNumber, p.vendorName, p.partNumber, p.partDescription, p.quantity, p.unitCost, p.totalCost, p.status, p.date]);
    downloadCSV(
      `po-summary-${startDate}-${endDate}.csv`,
      ['PO #', 'Vendor', 'Part #', 'Part Description', 'Quantity', 'Unit Cost', 'Total Cost', 'Status', 'Date'],
      rows
    );
  };

  // Export: Payment Summary grouped by vendor
  const exportPaymentSummary = () => {
    const rows = filteredPayments.map((p) => [p.paymentNumber, p.vendorName, p.amount, p.status, p.dueDate, p.paidDate ?? '', p.poNumber ?? '']);
    downloadCSV(
      `payment-summary-${startDate}-${endDate}.csv`,
      ['Payment #', 'Vendor', 'Amount', 'Status', 'Due Date', 'Paid Date', 'Linked PO #'],
      rows
    );
  };

  // Export: Shipping Cost Summary grouped by carrier
  const exportShippingSummary = () => {
    const rows = filteredShipping.map((s) => [s.shipmentNumber, s.carrier, s.cost, s.billedDate, s.ticketNumber ?? '']);
    downloadCSV(
      `shipping-summary-${startDate}-${endDate}.csv`,
      ['Shipment #', 'Carrier', 'Cost', 'Billed Date', 'Linked Ticket #'],
      rows
    );
  };

  // Export: Ticket Cost Report
  const exportTicketCosts = () => {
    const rows = filteredTickets.map((t) => [t.ticketNumber, t.partNumber, t.customerName, t.partCost, t.shippingCost, t.laborCost, t.totalCost, t.fulfillmentMethod]);
    downloadCSV(
      `ticket-costs-${startDate}-${endDate}.csv`,
      ['Ticket #', 'Part #', 'Customer', 'Part Cost', 'Shipping Cost', 'Labor Cost', 'Total Cost', 'Fulfillment Method'],
      rows
    );
  };

  // Monthly grouped bar chart
  const monthlyData = useMemo(() => {
    // Collect all months in range
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const months = [];
    let cur = start.startOf('month');
    while (cur.isBefore(end.add(1, 'day'))) {
      months.push(cur.format('YYYY-MM'));
      cur = cur.add(1, 'month');
    }

    const result = months.map((m) => {
      const mStart = `${m}-01`;
      const mEnd = dayjs(m).endOf('month').format('YYYY-MM-DD');
      const inM = (d) => Boolean(d) && d >= mStart && d <= mEnd;

      const poCosts = filteredPos.filter((p) => inM(p.date)).reduce((s, p) => s + p.totalCost, 0);
      const shippingCosts = filteredShipping.filter((s) => inM(s.billedDate)).reduce((s, c) => s + c.cost, 0);
      const laborCosts = filteredTickets.filter((t) => inM(t.date)).reduce((s, t) => s + t.laborCost, 0);
      return { month: dayjs(m).format('MMM YYYY'), poCosts, shippingCosts, laborCosts };
    });

    return result;
  }, [filteredPos, filteredShipping, filteredTickets, startDate, endDate]);

  const chartOptions = {
    chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: false }, background: 'transparent' },
    dataLabels: { enabled: false },
    grid: { borderColor: '#e3e8ef', strokeDashArray: 4 },
    tooltip: { theme: 'light', y: { formatter: (v) => fmt(v) } },
    xaxis: {
      categories: monthlyData.map((d) => d.month),
      labels: { style: { fontSize: '11px', colors: '#607d8b' } }
    },
    yaxis: {
      labels: {
        style: { fontSize: '11px', colors: '#607d8b' },
        formatter: (v) => `$${v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v.toFixed(0)}`
      }
    },
    colors: ['#009688', '#1565C0', '#E65100'],
    plotOptions: { bar: { columnWidth: '60%', borderRadius: 3 } },
    legend: {
      show: true,
      position: 'top',
      fontSize: '12px',
      labels: { colors: '#607d8b' }
    }
  };

  const chartSeries = [
  { name: 'PO Costs', data: monthlyData.map((d) => parseFloat(d.poCosts.toFixed(2))) },
  { name: 'Shipping Costs', data: monthlyData.map((d) => parseFloat(d.shippingCosts.toFixed(2))) },
  { name: 'Labor Costs', data: monthlyData.map((d) => parseFloat(d.laborCosts.toFixed(2))) }];

  const reports = [
  {
    title: 'Purchase Order Summary',
    description: `All ${filteredPos.length} purchase orders within ${dateLabel}, grouped by vendor with totals.`,
    onExport: exportPOSummary
  },
  {
    title: 'Supplier Payment Summary',
    description: `All ${filteredPayments.length} payments within ${dateLabel}, grouped by vendor.`,
    onExport: exportPaymentSummary
  },
  {
    title: 'Shipping Cost Summary',
    description: `All ${filteredShipping.length} shipping costs within ${dateLabel}, grouped by carrier.`,
    onExport: exportShippingSummary
  },
  {
    title: 'Cost per Ticket Report',
    description: `All ${filteredTickets.length} ticket costs within ${dateLabel} with fulfillment breakdown.`,
    onExport: exportTicketCosts
  }];

  return (
    <Box sx={{ pt: 3, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
        Financial Reports
      </Typography>

      {/* 2×2 report card grid */}
      <Box
        sx={{ display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2
        }}>
        
        {reports.map((r) =>
        <ReportCard key={r.title} title={r.title} description={r.description} onExport={r.onExport} />
        )}
      </Box>

      {/* Monthly cost breakdown chart */}
      <ContentCard
        sx={{ p: 3
        }}>
        
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>
          Monthly Cost Breakdown
        </Typography>
        <ReactApexChart
          type="bar"
          options={chartOptions}
          series={chartSeries}
          height={240}
          width="100%" />
        
      </ContentCard>
    </Box>);

}